import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Alert,
  Tabs,
  Tab,
  Button,
  Modal,
  Collapse,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import { useUserDifferences } from "./utils/use-user-difference";

// Change Indicator Component
const ChangeIndicator = ({ changeType, showLabel = true }) => {
  const config = {
    ADDED: {
      color: "success",
      icon: "fa-plus",
      label: "Added",
      bgClass: "bg-success text-white",
    },
    REMOVED: {
      color: "danger",
      icon: "fa-minus",
      label: "Removed",
      bgClass: "bg-danger text-white",
    },
    MODIFIED: {
      color: "warning",
      icon: "fa-edit",
      label: "Modified",
      bgClass: "bg-warning text-dark",
    },
    UNCHANGED: {
      color: "secondary",
      icon: null,
      label: "Unchanged",
      bgClass: "bg-secondary text-white",
    },
  };

  const { icon, label, bgClass } = config[changeType] || config.UNCHANGED;

  if (changeType === "UNCHANGED" && !showLabel) return null;

  const hasIcon = icon !== null;
  const hasLabel = showLabel && label;

  return (
    <span
      className={`badge ${bgClass}`}
      style={{
        fontSize: "0.7rem",
        padding: hasLabel ? "0.25rem 0.5rem" : "0.25rem",
        minWidth: hasIcon && !hasLabel ? "24px" : "auto",
      }}
    >
      {hasIcon && (
        <i
          className={`fa ${icon} ${hasLabel ? "mr-1" : ""}`}
          style={{ fontSize: "10px" }}
        ></i>
      )}
      {hasLabel && label}
    </span>
  );
};

// Comparison Field Component
const ComparisonField = ({
  label,
  before,
  after,
  changeType,
  icon,
  tooltip,
  formatValue = (val) => val,
}) => {
  const hasChanged = changeType && changeType !== "UNCHANGED";
  const formattedBefore = formatValue(before);
  const formattedAfter = formatValue(after);

  const tooltipElement = tooltip ? (
    <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
      <i
        className="fa fa-info-circle ml-1"
        style={{ opacity: 0.6, fontSize: "12px" }}
      ></i>
    </OverlayTrigger>
  ) : null;

  return (
    <Col xs={12} sm={6} md={4} className="mb-3">
      <div className="d-flex align-items-start">
        <div
          className={`mr-2 mt-1 ${hasChanged ? "primary-color" : "text-muted"}`}
        >
          <i className={`fa ${icon}`} style={{ fontSize: "14px" }}></i>
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center mb-1">
            <small
              className="text-muted font-weight-bold text-uppercase"
              style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
            >
              {label}
            </small>
            {tooltipElement}
            {hasChanged && (
              <span className="ml-1">
                <ChangeIndicator changeType={changeType} />
              </span>
            )}
          </div>

          <div className="d-flex flex-column gap-1">
            {hasChanged && before !== undefined && (
              <div className="d-flex align-items-center">
                <small className="text-muted mr-1" style={{ minWidth: "40px" }}>
                  From:
                </small>
                <small
                  className="text-danger"
                  style={{ textDecoration: "line-through", opacity: 0.8 }}
                >
                  {formattedBefore || "—"}
                </small>
              </div>
            )}
            <div className="d-flex align-items-center">
              {hasChanged && (
                <small className="text-muted mr-1" style={{ minWidth: "40px" }}>
                  To:
                </small>
              )}
              <small
                className={`font-weight-bold ${
                  hasChanged ? "text-success" : "text-base"
                }`}
                style={{ wordBreak: "break-word" }}
              >
                {formattedAfter || "—"}
              </small>
            </div>
          </div>
        </div>
      </div>
    </Col>
  );
};

// AccessChipList Component
const AccessChipList = ({
  items,
  color = "primary",
  maxVisible = 3,
  title,
  changeMap = {},
  accessChangeReason = "",
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const getChangeType = (itemId) => changeMap[itemId] || "UNCHANGED";

  const visibleItems = items?.slice(0, maxVisible) || [];
  const hasChanges = items?.some(
    (item) => getChangeType(item.id) !== "UNCHANGED"
  );

  const addedCount =
    items?.filter((item) => getChangeType(item.id) === "ADDED").length || 0;
  const removedCount =
    items?.filter((item) => getChangeType(item.id) === "REMOVED").length || 0;
  const unchangedCount = items?.length - addedCount - removedCount;

  return (
    <>
      <div>
        <small className="text-muted d-block mb-2">
          {accessChangeReason ? (
            <span className="text-info">
              <i className="fa fa-info-circle mr-1"></i>
              {accessChangeReason}
            </span>
          ) : hasChanges ? (
            `Changes: ${addedCount} added, ${removedCount} removed, ${unchangedCount} unchanged`
          ) : (
            "No changes in this section"
          )}
        </small>
        <div className="d-flex flex-wrap gap-2">
          {visibleItems.map((item, index) => {
            const changeType = getChangeType(item.id);
            const badgeClass =
              changeType === "UNCHANGED"
                ? `badge-${color} border`
                : changeType === "ADDED"
                ? "bg-success"
                : changeType === "REMOVED"
                ? "bg-danger"
                : "bg-warning";

            return (
              <span
                key={item.id || index}
                className={`badge ${badgeClass} ${
                  changeType === "UNCHANGED" ? "" : "text-white"
                } mr-1 mb-1`}
                style={{
                  fontWeight: 600,
                  borderWidth: "1.5px",
                  fontSize: "0.75rem",
                  padding: "0.25rem 0.5rem",
                }}
              >
                {changeType !== "UNCHANGED" && (
                  <ChangeIndicator changeType={changeType} showLabel={false} />
                )}
                {item.name}
              </span>
            );
          })}
          {items?.length > maxVisible && (
            <span
              className="badge border badge-outline"
              style={{
                cursor: "pointer",
                fontWeight: 600,
                borderStyle: "dashed",
                fontSize: "0.75rem",
                padding: "0.25rem 0.5rem",
              }}
              onClick={() => setModalOpen(true)}
            >
              View All ({items?.length})
            </span>
          )}
        </div>
      </div>

      {/* Modal for viewing all items */}
      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg">
        <Modal.Header closeButton className="background-primary">
          <Modal.Title className="d-flex align-items-center">
            <div
              className="bg-primary rounded-circle d-flex align-items-center justify-content-center mr-2"
              style={{ width: "32px", height: "32px" }}
            >
              <i
                className="fa fa-eye text-white"
                style={{ fontSize: "14px" }}
              ></i>
            </div>
            <span className="color-dark">
              {title} ({items?.length})
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="background-primary">
          {accessChangeReason && (
            <Alert variant="info" className="mb-3">
              <small>{accessChangeReason}</small>
            </Alert>
          )}
          <Row
            className="fancy-scroll"
            style={{ maxHeight: "400px", overflow: "auto" }}
          >
            {items?.map((item, index) => {
              const changeType = getChangeType(item.id);
              return (
                <Col xs={12} sm={6} key={item.id || index} className="mb-2">
                  <div className="card card-secondary border mb-0 p-0">
                    <div className="card-body py-1">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle mr-2"
                            style={{
                              width: "8px",
                              height: "8px",
                              backgroundColor: `var(--${color})`,
                            }}
                          ></div>
                          <span
                            className="font-weight-bold color-dark"
                            style={{ fontSize: "0.875rem" }}
                          >
                            {item.name}
                          </span>
                        </div>
                        {changeType !== "UNCHANGED" && (
                          <ChangeIndicator changeType={changeType} showLabel />
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Modal.Body>
        <Modal.Footer className="background-primary">
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Resource Group Component
const ResourceGroup = ({ resourceGroup, accessChangeReason = "" }) => {
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const {
    resourceType,
    before = [],
    after = [],
    changes = [],
    hasChanges,
  } = resourceGroup;

  const primaryItems = changes.slice(0, 6);
  const extraItems = changes.slice(6);
  const hasExtra = extraItems.length > 0;

  const getBorderColor = (status) => {
    switch (status) {
      case "ADDED":
        return "#28a745"; // success green
      case "REMOVED":
        return "#dc3545"; // danger red
      default:
        return "#dee2e6"; // default border
    }
  };

  return (
    <Card className="card-primary border mb-3">
      <Card.Body>
        <div className="d-flex flex-column gap-3">
          {/* Resource Type Header */}
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className={`rounded-circle d-flex align-items-center justify-content-center mr-3 ${
                  hasChanges ? "bg-warning" : "bg-primary"
                }`}
                style={{ width: "32px", height: "32px" }}
              >
                <i
                  className={`fa fa-sitemap text-white`}
                  style={{ fontSize: "14px" }}
                ></i>
              </div>
              <div>
                <h6 className="font-weight-bold mb-0 color-dark">
                  {resourceType}
                </h6>
                <small className="text-muted">
                  {before.length} before → {after.length} after
                  {hasChanges &&
                    ` • ${
                      changes.filter((c) => c.status === "ADDED").length
                    } added, ${
                      changes.filter((c) => c.status === "REMOVED").length
                    } removed`}
                  {accessChangeReason && (
                    <span className="text-info ml-2">
                      <i className="fa fa-info-circle"></i>
                      {accessChangeReason}
                    </span>
                  )}
                </small>
              </div>
            </div>
            <div className="d-flex align-items-center">
              {hasChanges && <ChangeIndicator changeType="MODIFIED" />}
              {hasExtra && (
                <button
                  className="btn btn-link p-0 ml-2 primary-color"
                  onClick={() => setExpanded(!expanded)}
                >
                  <i
                    className={`fa ${
                      expanded ? "fa-chevron-up" : "fa-chevron-down"
                    }`}
                  ></i>
                </button>
              )}
            </div>
          </div>

          {/* Resource Items */}
          <Row>
            {primaryItems.map((change, index) => (
              <Col
                xs={12}
                sm={6}
                md={4}
                key={change.id ?? `p-${index}`}
                className="mb-2"
              >
                <div
                  className="card card-secondary border mb-0 p-0"
                  style={{
                    borderLeft: `4px solid ${getBorderColor(change.status)}`,
                  }}
                >
                  <div className="card-body py-1">
                    <div className="d-flex align-items-center justify-content-between">
                      <span
                        className="font-weight-bold color-dark text-truncate"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {change.name}
                      </span>
                      <ChangeIndicator
                        changeType={change.status}
                        showLabel={false}
                      />
                    </div>
                  </div>
                </div>
              </Col>
            ))}

            {/* Collapsible extra items */}
            {hasExtra && (
              <Collapse in={expanded} className="w-100 ml-1">
                <Row className="mt-1">
                  {extraItems.map((change, index) => (
                    <Col
                      xs={12}
                      sm={6}
                      md={4}
                      key={change.id ?? `e-${index}`}
                      className="mb-2"
                    >
                      <div
                        className="card card-secondary border mb-0 p-0"
                        style={{
                          borderLeft: `4px solid ${getBorderColor(
                            change.status
                          )}`,
                        }}
                      >
                        <div className="card-body py-1">
                          <div className="d-flex align-items-center justify-content-between">
                            <span
                              className="font-weight-bold color-dark text-truncate"
                              style={{ fontSize: "0.875rem" }}
                            >
                              {change.name}
                            </span>
                            <ChangeIndicator
                              changeType={change.status}
                              showLabel={false}
                            />
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Collapse>
            )}
          </Row>

          {/* View All Button for large lists */}
          {changes.length > 6 && !expanded && (
            <Button
              variant="outline-primary"
              onClick={() => setModalOpen(true)}
              className="align-self-start"
            >
              View All {changes.length} Resources
            </Button>
          )}
        </div>
      </Card.Body>

      {/* Modal for viewing all resources */}
      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg">
        <Modal.Header closeButton className="background-primary">
          <Modal.Title className="d-flex align-items-center">
            <div
              className="bg-primary rounded-circle d-flex align-items-center justify-content-center mr-2"
              style={{ width: "32px", height: "32px" }}
            >
              <i
                className="fa fa-eye text-white"
                style={{ fontSize: "14px" }}
              ></i>
            </div>
            <span className="color-dark">
              {resourceType} - All Resources ({changes.length})
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="background-primary">
          {accessChangeReason && (
            <Alert variant="info" className="mb-3">
              <small>{accessChangeReason}</small>
            </Alert>
          )}
          <Row className="mt-2">
            {changes.map((change, index) => (
              <Col
                xs={12}
                sm={6}
                md={4}
                key={change.id ?? `all-${index}`}
                className="mb-3"
              >
                <div
                  className="card card-secondary border mb-0 p-0"
                  style={{
                    borderLeft: `4px solid ${getBorderColor(change.status)}`,
                  }}
                >
                  <div className="card-body py-1">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="font-weight-bold color-dark">
                        {change.name}
                      </div>
                      <ChangeIndicator changeType={change.status} showLabel />
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer className="background-primary">
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

// SectionCard Component
const SectionCard = ({
  title,
  icon,
  children,
  count,
  hasChanges = false,
  defaultExpanded = false,
  accessNote = "",
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Card className="card-primary border mb-3" style={{ borderRadius: "1rem" }}>
      <Card.Body className="p-4">
        <div className="d-flex flex-column gap-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <button
                className="btn btn-link p-0 mr-3"
                onClick={() => setExpanded(!expanded)}
                style={{
                  color: hasChanges ? "var(--warning)" : "var(--primary)",
                }}
              >
                <i
                  className={`fa ${
                    expanded ? "fa-chevron-up" : "fa-chevron-down"
                  }`}
                ></i>
              </button>
              <div
                className={`rounded-circle d-flex align-items-center justify-content-center mr-3 ${
                  hasChanges ? "bg-warning" : "bg-primary"
                }`}
                style={{ width: "36px", height: "36px" }}
              >
                <i
                  className={`fa ${icon} text-white`}
                  style={{ fontSize: "14px" }}
                ></i>
              </div>
              <div>
                <h6 className="font-weight-bold mb-0 color-dark">{title}</h6>
                {accessNote && (
                  <small className="text-info">
                    <i className="fa fa-info-circle mr-1"></i>
                    {accessNote}
                  </small>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center">
              {count !== undefined && (
                <span
                  className={`badge ${
                    hasChanges ? "bg-warning" : "bg-primary"
                  } text-white`}
                  style={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    minWidth: "32px",
                    padding: "0.25rem 0.5rem",
                  }}
                >
                  {count}
                </span>
              )}
            </div>
          </div>

          <Collapse in={expanded} className="mt-1 ml-4">
            <div>{children}</div>
          </Collapse>
        </div>
      </Card.Body>
    </Card>
  );
};

// Access Restriction Alert Component
const AccessRestrictionAlert = ({
  isRestricted,
  beforeRestricted,
  afterRestricted,
}) => {
  const restrictionChanged = beforeRestricted !== afterRestricted;

  if (restrictionChanged) {
    const becomingRestricted = afterRestricted && !beforeRestricted;

    return (
      <Alert variant="warning" className="border-radius-3 mb-4">
        <div className="d-flex align-items-center">
          <i
            className="fa fa-exchange-alt mr-2"
            style={{ fontSize: "1.2rem" }}
          ></i>
          <div>
            <h6 className="font-weight-bold mb-1">Access Level Changed</h6>
            <small className="mb-0">
              {becomingRestricted ? (
                <>
                  User is becoming <strong>Restricted</strong>. Regions,
                  Resources, and Recon Accounts will be assigned with limited
                  permissions. System Reports will no longer be accessible.
                </>
              ) : (
                <>
                  User is becoming <strong>Standard</strong>. All regional,
                  resource, and recon account restrictions will be removed. User
                  will gain access to System Reports.
                </>
              )}
            </small>
          </div>
        </div>
      </Alert>
    );
  }

  return (
    <Alert
      variant={isRestricted ? "warning" : "info"}
      className="border-radius-3 mb-4"
    >
      <div className="d-flex align-items-center">
        <i
          className={`fa ${isRestricted ? "fa-lock mr-2" : "fa-unlock mr-2"}`}
          style={{ fontSize: "1.2rem" }}
        ></i>
        <div>
          <h6 className="font-weight-bold mb-1">
            {isRestricted ? "Restricted User Access" : "Standard User Access"}
          </h6>
          <small className="mb-0">
            {isRestricted
              ? "This user has restricted access with assigned regions, resources, and recon accounts. Only User Reports are available."
              : "This user has standard access with no regional/resource restrictions. Both User Reports and System Reports are available."}
          </small>
        </div>
      </div>
    </Alert>
  );
};

// Main UserUpdate Component
const UserUpdate = ({ workQueueData }) => {
  const [tabValue, setTabValue] = useState(0);
  const { differences, structuredUserBefore, structuredUserAfter } =
    useUserDifferences(workQueueData);

  const isLoading = !differences;

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "200px" }}
      >
        <div className="spinner-border primary-color" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  const {
    basicInfo,
    roles,
    regions,
    userReportAccess,
    systemReportAccess,
    userAccess,
    hasAnyChanges,
    summary,
  } = differences;

  // Determine current and previous restriction status
  const isUserRestricted =
    structuredUserAfter?.restrictedUser ??
    structuredUserBefore?.restrictedUser ??
    false;
  const wasUserRestricted = structuredUserBefore?.restrictedUser ?? false;
  const restrictionChanged = wasUserRestricted !== isUserRestricted;

  const formatRestricted = (restricted) => {
    return restricted ? "Yes - Restricted Access" : "No - Standard Access";
  };

  const getTeamName = (teamId) => {
    return teamId?.name || teamId?.value || `Team ${teamId}`;
  };

  // Create change maps for chip lists
  const createChangeMap = (items) => {
    const map = {};
    items?.forEach((item) => {
      if (item.status && item.status !== "UNCHANGED") {
        map[item.id] = item.status;
      }
    });
    return map;
  };

  // Generate access change reasons
  const getRegionsAccessReason = () => {
    if (restrictionChanged) {
      if (isUserRestricted) {
        return "Regions are now being assigned because user is becoming restricted";
      } else {
        const removedCount =
          regions?.allItems?.filter((item) => item.status === "REMOVED")
            .length || 0;
        return removedCount > 0
          ? `All ${removedCount} regions removed because user is no longer restricted`
          : "Regions no longer assigned to standard users";
      }
    }
    return isUserRestricted
      ? "Regions assigned for restricted user access"
      : "";
  };

  const getResourcesAccessReason = () => {
    if (restrictionChanged) {
      if (isUserRestricted) {
        return "Resources and recon accounts are now being assigned because user is becoming restricted";
      } else {
        const totalResources =
          userAccess?.resourceGroups?.reduce(
            (total, group) => total + (group.before?.length || 0),
            0
          ) || 0;
        return totalResources > 0
          ? `All ${totalResources} resources and recon accounts removed because user is no longer restricted`
          : "Resources and recon accounts no longer assigned to standard users";
      }
    }
    return isUserRestricted
      ? "Resources and recon accounts assigned for restricted user access"
      : "";
  };

  const getSystemReportsAccessReason = () => {
    if (restrictionChanged) {
      if (isUserRestricted) {
        const removedCount =
          systemReportAccess?.allItems?.filter(
            (item) => item.status === "REMOVED"
          ).length || 0;
        return removedCount > 0
          ? `System reports access removed because user is now restricted`
          : "System reports not available for restricted users";
      } else {
        return "System reports now available because user has standard access";
      }
    }
    return !isUserRestricted
      ? "System reports available for standard users"
      : "System reports not available for restricted users";
  };

  // User Header Component
  const UserHeader = () => (
    <div
      className="card card-primary border mb-4"
      style={{ borderRadius: "1rem" }}
    >
      <div className="card-body p-4">
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
          <div
            className="bg-primary rounded-circle d-flex align-items-center justify-content-center mr-md-4 mb-3 mb-md-0"
            style={{ width: "64px", height: "64px" }}
          >
            <span
              className="text-white font-weight-bold"
              style={{ fontSize: "1.5rem" }}
            >
              {structuredUserAfter?.firstName?.[0] ||
                structuredUserBefore?.firstName?.[0]}
              {structuredUserAfter?.lastName?.[0] ||
                structuredUserBefore?.lastName?.[0]}
            </span>
          </div>
          <div className="flex-grow-1">
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mb-2">
              <h4 className="font-weight-bold mb-2 mb-sm-0 mr-3 color-dark">
                {`${
                  structuredUserAfter?.firstName ||
                  structuredUserBefore?.firstName ||
                  ""
                } ${
                  structuredUserAfter?.lastName ||
                  structuredUserBefore?.lastName ||
                  ""
                }`.trim()}
              </h4>
              <Badge variant="primary" className="d-flex align-items-center">
                <i className="fa fa-edit mr-1"></i>
                {workQueueData?.workType || "USER UPDATE"}
              </Badge>
            </div>
            <div className="d-flex flex-column flex-sm-row flex-wrap align-items-start">
              <div className="d-flex align-items-center mb-1 mb-sm-0 mr-3">
                <i className="fa fa-envelope text-muted mr-1"></i>
                <span className="text-muted">
                  {structuredUserBefore?.username}
                </span>
              </div>
              <div className="d-flex align-items-center mb-1 mb-sm-0 mr-3">
                <i className="fa fa-id-badge text-muted mr-1"></i>
                <span className="text-muted">
                  {structuredUserAfter?.empCode ||
                    structuredUserBefore?.empCode}
                </span>
              </div>
              <Badge
                variant={isUserRestricted ? "warning" : "outline-secondary"}
                className="d-flex align-items-center"
              >
                <i
                  className={`fa ${
                    isUserRestricted ? "fa-lock mr-1" : "fa-unlock mr-1"
                  }`}
                ></i>
                {isUserRestricted ? "Restricted Access" : "Standard Access"}
                {restrictionChanged && (
                  <i className="fa fa-exchange-alt ml-1"></i>
                )}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-100">
      <UserHeader />

      {/* Access Restriction Alert */}
      <AccessRestrictionAlert
        isRestricted={isUserRestricted}
        beforeRestricted={wasUserRestricted}
        afterRestricted={isUserRestricted}
      />

      {/* Summary Card */}
      <Alert
        variant="info"
        className="border-radius-3 mb-4"
        style={{ border: "1px solid var(--info)" }}
      >
        <div className="d-flex flex-column w-100">
          <h5 className="font-weight-bold">User Update Summary</h5>
          <hr className="my-2" style={{ borderColor: "var(--info)" }} />
          <small className="mb-3">
            This work item will update the user with the following changes:
          </small>

          <Row className="mt-2">
            {summary?.basicInfoChanges > 0 && (
              <Col xs={12} sm={6} md={3} className="mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className="bg-warning rounded-circle mr-2"
                    style={{ width: "8px", height: "8px" }}
                  ></div>
                  <small className="font-weight-bold">
                    {summary?.basicInfoChanges} Basic Info Changes
                  </small>
                </div>
              </Col>
            )}

            {(summary?.rolesAdded > 0 || summary?.rolesRemoved > 0) && (
              <Col xs={12} sm={6} md={3} className="mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className="bg-warning rounded-circle mr-2"
                    style={{ width: "8px", height: "8px" }}
                  ></div>
                  <small className="font-weight-bold">
                    {roles?.allItems?.length} Roles (+{summary?.rolesAdded}/ -
                    {summary?.rolesRemoved})
                  </small>
                </div>
              </Col>
            )}

            {/* Show regions changes only if there are actual changes or user is restricted */}
            {(regions?.hasChanges || isUserRestricted) && (
              <Col xs={12} sm={6} md={3} className="mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className={`rounded-circle mr-2 ${
                      regions?.hasChanges ? "bg-warning" : "bg-secondary"
                    }`}
                    style={{ width: "8px", height: "8px" }}
                  ></div>
                  <small className="font-weight-bold">
                    {regions?.allItems.length} Regions
                    {regions?.hasChanges && (
                      <>
                        {" "}
                        (+{summary?.regionsAdded}/ -{summary?.regionsRemoved})
                      </>
                    )}
                    {isUserRestricted && (
                      <span className="badge badge-warning ml-1">
                        Restricted
                      </span>
                    )}
                  </small>
                </div>
              </Col>
            )}

            {(summary?.userReportsAdded > 0 ||
              summary?.userReportsRemoved > 0) && (
              <Col xs={12} sm={6} md={3} className="mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className="bg-warning rounded-circle mr-2"
                    style={{ width: "8px", height: "8px" }}
                  ></div>
                  <small className="font-weight-bold">
                    {userReportAccess?.allItems.length} User Reports (+
                    {summary?.userReportsAdded}/-{summary?.userReportsRemoved})
                  </small>
                </div>
              </Col>
            )}

            {/* Show system reports changes only if there are actual changes or user is not restricted */}
            {(systemReportAccess?.hasChanges || !isUserRestricted) && (
              <Col xs={12} sm={6} md={3} className="mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className={`rounded-circle mr-2 ${
                      systemReportAccess?.hasChanges
                        ? "bg-warning"
                        : "bg-secondary"
                    }`}
                    style={{ width: "8px", height: "8px" }}
                  ></div>
                  <small className="font-weight-bold">
                    {systemReportAccess?.allItems.length} System Reports
                    {systemReportAccess?.hasChanges && (
                      <>
                        {" "}
                        (+{summary?.systemReportsAdded}/ -
                        {summary?.systemReportsRemoved})
                      </>
                    )}
                  </small>
                </div>
              </Col>
            )}

            {/* Show resources changes only if there are actual changes or user is restricted */}
            {(userAccess?.hasChanges || isUserRestricted) && (
              <Col xs={12} sm={6} md={3} className="mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className={`rounded-circle mr-2 ${
                      userAccess?.hasChanges ? "bg-warning" : "bg-secondary"
                    }`}
                    style={{ width: "8px", height: "8px" }}
                  ></div>
                  <small className="font-weight-bold">
                    {summary?.resourceTypesChanged} Resource Types
                    {userAccess?.hasChanges && (
                      <>
                        {" "}
                        (+{summary?.resourcesAdded}/-{summary?.resourcesRemoved}
                        )
                      </>
                    )}
                    {isUserRestricted && (
                      <span className="badge badge-warning ml-1">
                        Restricted
                      </span>
                    )}
                  </small>
                </div>
              </Col>
            )}
          </Row>

          {!hasAnyChanges && (
            <small className="mt-2 font-italic">
              No changes detected in this user update.
            </small>
          )}
        </div>
      </Alert>

      {/* Tabs for Main Sections */}
      <Card
        className="card-primary border mt-4"
        style={{ borderRadius: "1rem" }}
      >
        <Card.Header className="bg-transparent border-bottom-0 p-1">
          <Tabs
            activeKey={tabValue}
            onSelect={(k) => setTabValue(Number(k))}
            className="border-bottom"
          >
            <Tab eventKey={0} title="Basic Information" className="border-0" />
            <Tab eventKey={1} title="Details" className="border-0" />
          </Tabs>
        </Card.Header>
        <Card.Body className="p-0">
          {/* Tab 1: Basic Information */}
          {tabValue === 0 && (
            <div className="p-3">
              <SectionCard
                title="User Details"
                icon="fa-user"
                hasChanges={basicInfo?.hasChanges}
                defaultExpanded={true}
              >
                <Row>
                  <ComparisonField
                    icon="fa-envelope"
                    label="Email"
                    before={structuredUserBefore?.username}
                    after={structuredUserBefore?.username}
                    changeType={"UNCHANGED"}
                    tooltip="Primary login identifier"
                  />
                  <ComparisonField
                    icon="fa-id-badge"
                    label="Employee Code"
                    before={structuredUserBefore?.empCode}
                    after={structuredUserAfter?.empCode}
                    changeType={
                      basicInfo?.changes.empCode?.status || "UNCHANGED"
                    }
                  />
                  <ComparisonField
                    icon="fa-user"
                    label="First Name"
                    before={structuredUserBefore?.firstName}
                    after={structuredUserAfter?.firstName}
                    changeType={
                      basicInfo?.changes.firstName?.status || "UNCHANGED"
                    }
                  />
                  <ComparisonField
                    icon="fa-user"
                    label="Middle Name"
                    before={structuredUserBefore?.middleName}
                    after={structuredUserAfter?.middleName}
                    changeType={
                      basicInfo?.changes.middleName?.status || "UNCHANGED"
                    }
                  />
                  <ComparisonField
                    icon="fa-user"
                    label="Last Name"
                    before={structuredUserBefore?.lastName}
                    after={structuredUserAfter?.lastName}
                    changeType={
                      basicInfo?.changes.lastName?.status || "UNCHANGED"
                    }
                  />
                  <ComparisonField
                    icon="fa-briefcase"
                    label="Designation"
                    before={structuredUserBefore?.designation}
                    after={structuredUserAfter?.designation}
                    changeType={
                      basicInfo?.changes.designation?.status || "UNCHANGED"
                    }
                  />
                  <ComparisonField
                    icon="fa-phone"
                    label="Mobile Number"
                    before={structuredUserBefore?.mobileNo}
                    after={structuredUserAfter?.mobileNo}
                    changeType={
                      basicInfo?.changes.mobileNo?.status || "UNCHANGED"
                    }
                  />
                  <ComparisonField
                    icon="fa-phone"
                    label="Office Phone"
                    before={structuredUserBefore?.officePhone}
                    after={structuredUserAfter?.officePhone}
                    changeType={
                      basicInfo?.changes.officePhone?.status || "UNCHANGED"
                    }
                  />
                  <ComparisonField
                    icon="fa-building"
                    label="Office Extension"
                    before={structuredUserBefore?.officePhoneExt}
                    after={structuredUserAfter?.officePhoneExt}
                    changeType={
                      basicInfo?.changes.officePhoneExt?.status || "UNCHANGED"
                    }
                  />
                  <ComparisonField
                    icon="fa-users"
                    label="Team"
                    before={getTeamName(structuredUserBefore?.teamId)}
                    after={getTeamName(structuredUserAfter?.teamId)}
                    changeType={
                      basicInfo?.changes.teamId?.status || "UNCHANGED"
                    }
                    tooltip="Assigned team for collaboration and workflow"
                  />
                  <ComparisonField
                    icon="fa-lock"
                    label="Restricted User"
                    before={formatRestricted(wasUserRestricted)}
                    after={formatRestricted(isUserRestricted)}
                    changeType={restrictionChanged ? "MODIFIED" : "UNCHANGED"}
                    tooltip={
                      restrictionChanged
                        ? "User access level is changing, which affects regions, resources, and report access"
                        : isUserRestricted
                        ? "Restricted users have regions, resources, and recon accounts assigned with limited access"
                        : "Standard users have full system access without regional/resource restrictions"
                    }
                  />
                </Row>
              </SectionCard>
            </div>
          )}

          {/* Tab 2: Access & Permissions */}
          {tabValue === 1 && (
            <div className="p-3">
              {/* Roles - Always visible */}
              <SectionCard
                title="Roles & Permissions"
                icon="fa-shield"
                count={roles?.allItems.length}
                hasChanges={roles?.hasChanges}
              >
                <AccessChipList
                  items={roles?.allItems}
                  color="primary"
                  maxVisible={5}
                  title="Roles"
                  changeMap={createChangeMap(roles?.allItems)}
                />
              </SectionCard>

              {/* Regions - Show with proper context */}
              <SectionCard
                title="Regions"
                icon="fa-map-marker"
                count={regions?.allItems.length}
                hasChanges={regions?.hasChanges}
                accessNote={
                  isUserRestricted
                    ? "Assigned for restricted access"
                    : "Not assigned to standard users"
                }
              >
                {isUserRestricted || regions?.hasChanges ? (
                  <AccessChipList
                    items={regions?.allItems}
                    color="primary"
                    maxVisible={5}
                    title="Regions"
                    changeMap={createChangeMap(regions?.allItems)}
                    accessChangeReason={getRegionsAccessReason()}
                  />
                ) : (
                  <Alert variant="info" className="mb-0">
                    <div className="d-flex align-items-center">
                      <i className="fa fa-info-circle mr-2"></i>
                      <div>
                        <h6 className="font-weight-bold mb-1">
                          No Regions Assigned
                        </h6>
                        <small className="mb-0">
                          Regions are only assigned to restricted users. This
                          user has standard access.
                        </small>
                      </div>
                    </div>
                  </Alert>
                )}
              </SectionCard>

              {/* User Reports - Always visible */}
              <SectionCard
                title="User Reports"
                icon="fa-file-text"
                count={userReportAccess?.allItems.length}
                hasChanges={userReportAccess?.hasChanges}
              >
                <AccessChipList
                  items={userReportAccess?.allItems}
                  color="primary"
                  maxVisible={5}
                  title="User Reports"
                  changeMap={createChangeMap(userReportAccess?.allItems)}
                />
              </SectionCard>

              {/* System Reports - Show with proper context */}
              <SectionCard
                title="System Reports"
                icon="fa-file-text"
                count={systemReportAccess?.allItems.length}
                hasChanges={systemReportAccess?.hasChanges}
                accessNote={
                  !isUserRestricted
                    ? "Available for standard users"
                    : "Not available for restricted users"
                }
              >
                {!isUserRestricted || systemReportAccess?.hasChanges ? (
                  <AccessChipList
                    items={systemReportAccess?.allItems}
                    color="primary"
                    maxVisible={5}
                    title="System Reports"
                    changeMap={createChangeMap(systemReportAccess?.allItems)}
                    accessChangeReason={getSystemReportsAccessReason()}
                  />
                ) : (
                  <Alert variant="info" className="mb-0">
                    <div className="d-flex align-items-center">
                      <i className="fa fa-info-circle mr-2"></i>
                      <div>
                        <h6 className="font-weight-bold mb-1">
                          System Reports Not Available
                        </h6>
                        <small className="mb-0">
                          System reports are only available for standard users.
                          This user has restricted access.
                        </small>
                      </div>
                    </div>
                  </Alert>
                )}
              </SectionCard>

              {/* Resources & Recon Accounts - Show with proper context */}
              <SectionCard
                title="Resources & Recon Accounts"
                icon="fa-sitemap"
                count={userAccess?.resourceGroups?.reduce(
                  (total, group) => total + (group?.changes?.length || 0),
                  0
                )}
                hasChanges={userAccess?.hasChanges}
                accessNote={
                  isUserRestricted
                    ? "Resources and recon accounts are assigned to this user for restricted access."
                    : "Resources and recon accounts are only assigned to restricted users."
                }
              >
                {isUserRestricted || userAccess?.hasChanges ? (
                  userAccess?.resourceGroups?.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                      {userAccess?.resourceGroups.map(
                        (resourceGroup, index) => (
                          <ResourceGroup
                            key={index}
                            resourceGroup={resourceGroup}
                            accessChangeReason={getResourcesAccessReason()}
                          />
                        )
                      )}
                    </div>
                  ) : (
                    <Alert variant="warning" className="mb-0">
                      <div className="d-flex align-items-center">
                        <i className="fa fa-exclamation-triangle mr-2"></i>
                        <div>
                          <h6 className="font-weight-bold mb-1">
                            No Resources Assigned
                          </h6>
                          <small className="mb-0">
                            {isUserRestricted
                              ? "This restricted user does not have any resources or recon accounts assigned."
                              : "Resources and recon accounts are only assigned to restricted users."}
                          </small>
                        </div>
                      </div>
                    </Alert>
                  )
                ) : (
                  <Alert variant="info" className="mb-0">
                    <div className="d-flex align-items-center">
                      <i className="fa fa-info-circle mr-2"></i>
                      <div>
                        <h6 className="font-weight-bold mb-1">
                          Resources & Recon Accounts Not Assigned
                        </h6>
                        <small className="mb-0">
                          Resources and recon accounts are only assigned to
                          restricted users. This user has standard access.
                        </small>
                      </div>
                    </div>
                  </Alert>
                )}
              </SectionCard>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default UserUpdate;
